import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import { useTranslation } from 'react-i18next';

import { InfoRow } from 'common/components/InfoRow';
import {
    IGroupConditionUpdateFragment,
    IOfferEventDetailFragment,
    IOfferEventEnrollmentCurrentUserFragment,
} from 'graphql/types';
import { filterExtraCategoryValuesForOfferEvent } from 'offer/utils/offerEvent';
import { getScoringDisplay } from 'common/utils/scoring';
import { CertificateInfoRow } from 'common/components/CertificateInfoRow';
import { Link } from 'common/components/Link';
import { useFeature } from 'common/hooks/useFeature';

import { ExtraCategoryInfo } from './ExtraCategoryInfo';
import { SubscriptionStatusInfo } from './SubscriptionStatusInfo';
import { GroupConditionsSection } from './GroupConditionsSection';

interface IProps {
    groupConditions?: IGroupConditionUpdateFragment[];
    offerEvent: IOfferEventDetailFragment;
    showSubscriptionStatusSection?: boolean;
    currentUserEnrollment?: IOfferEventEnrollmentCurrentUserFragment;
}

export const BasicInfoSection = ({
    groupConditions,
    offerEvent,
    showSubscriptionStatusSection,
}: IProps) => {
    const [translate] = useTranslation();
    const { showChat } = useFeature(undefined, true);

    const {
        currentUserEnrollment,
        offer: { extraCategoryValues },
        trainers: trainerConnection,
        waitingList,
        scoring,
        certificate,
    } = offerEvent;

    const trainers =
        trainerConnection?.edges.map((edge) => edge?.node).filter(Boolean) ||
        [];

    // Filter extra category values for offer event
    const extraCategories =
        filterExtraCategoryValuesForOfferEvent(extraCategoryValues);

    return (
        <Stack spacing={2}>
            {!!scoring?.length && (
                <InfoRow description={translate('offerEventPoints')}>
                    <Box>{getScoringDisplay(scoring)}</Box>
                </InfoRow>
            )}

            {showSubscriptionStatusSection && currentUserEnrollment && (
                <SubscriptionStatusInfo
                    subscription={currentUserEnrollment}
                    waitingList={waitingList}
                />
            )}

            {!!certificate && <CertificateInfoRow certificate={certificate} />}

            {!!trainers.length && (
                <InfoRow description={translate('by')}>
                    <Stack spacing={1}>
                        {trainers.map((trainer) =>
                            showChat ? (
                                <Link
                                    key={trainer.id}
                                    params={{ id: trainer.id }}
                                    to="USER_CHAT"
                                >
                                    {trainer.name}
                                </Link>
                            ) : (
                                <Box component="span" key={trainer.id}>
                                    {trainer.name}
                                </Box>
                            )
                        )}
                    </Stack>
                </InfoRow>
            )}

            {!!extraCategories?.length && (
                <ExtraCategoryInfo extraCategoryValues={extraCategories} />
            )}

            {!!groupConditions?.length && (
                <InfoRow
                    description={translate(
                        'offerEventDetail.groupConditions.groupConditionsTitle'
                    )}
                >
                    <Box sx={{ mt: -0.75 }}>
                        <GroupConditionsSection
                            groupConditions={groupConditions}
                        />
                    </Box>
                </InfoRow>
            )}
        </Stack>
    );
};
